import { useState, useEffect, useRef } from 'react';
import { MessageSquare, Palette, Code, Rocket, CheckCircle, ArrowRight } from 'lucide-react';

// Different process steps for different services
const processSteps = {
  'website-development': [
    { icon: MessageSquare, title: 'Discovery', desc: 'We discuss your vision, goals, and requirements', color: '#3B82F6' },
    { icon: Palette, title: 'Design', desc: 'Creating beautiful mockups and wireframes', color: '#8B5CF6' },
    { icon: Code, title: 'Development', desc: 'Building your website with clean, efficient code', color: '#F97316' },
    { icon: Rocket, title: 'Launch', desc: 'Testing, optimization, and going live', color: '#10B981' }
  ],
  'app-development': [
    { icon: MessageSquare, title: 'Planning', desc: 'Understanding your app requirements and user needs', color: '#3B82F6' },
    { icon: Palette, title: 'UI/UX Design', desc: 'Crafting intuitive and engaging interfaces', color: '#8B5CF6' },
    { icon: Code, title: 'Development', desc: 'Agile development with regular updates', color: '#F97316' },
    { icon: Rocket, title: 'Deployment', desc: 'App store submission and launch support', color: '#10B981' }
  ],
  'uiux-design': [
    { icon: MessageSquare, title: 'Research', desc: 'Understanding users and market analysis', color: '#3B82F6' },
    { icon: Palette, title: 'Wireframes', desc: 'Creating the blueprint of your design', color: '#8B5CF6' },
    { icon: Code, title: 'Prototyping', desc: 'Interactive prototypes for testing', color: '#F97316' },
    { icon: CheckCircle, title: 'Delivery', desc: 'Final designs with full documentation', color: '#10B981' }
  ],
  'ai-solutions': [
    { icon: MessageSquare, title: 'Analysis', desc: 'Understanding your AI needs and data', color: '#3B82F6' },
    { icon: Code, title: 'Model Design', desc: 'Architecting the perfect AI solution', color: '#8B5CF6' },
    { icon: Rocket, title: 'Training', desc: 'Training and fine-tuning AI models', color: '#F97316' },
    { icon: CheckCircle, title: 'Integration', desc: 'Seamless integration into your workflow', color: '#10B981' }
  ],
  'business-tools': [
    { icon: MessageSquare, title: 'Assessment', desc: 'Analyzing your business processes', color: '#3B82F6' },
    { icon: Palette, title: 'Design', desc: 'Designing custom solutions for your needs', color: '#8B5CF6' },
    { icon: Code, title: 'Build', desc: 'Developing robust business tools', color: '#F97316' },
    { icon: Rocket, title: 'Deploy', desc: 'Training and deployment support', color: '#10B981' }
  ]
};

const HowItWorksAnimated = ({ serviceId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const sectionRef = useRef(null);
  const intervalRef = useRef(null);

  const steps = processSteps[serviceId] || processSteps['website-development'];

  // Intersection Observer for animation trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-advance steps
  useEffect(() => {
    if (isVisible) {
      intervalRef.current = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % steps.length);
        setProgress(0);
      }, 4000);

      // Progress bar animation
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 2.5, 100));
      }, 100);

      return () => {
        clearInterval(intervalRef.current);
        clearInterval(progressInterval);
      };
    }
  }, [isVisible, steps.length]);

  // Reset progress when step changes
  useEffect(() => {
    setProgress(0);
  }, [activeStep]);

  const handleStepClick = (index) => {
    setActiveStep(index);
    setProgress(0);
    // Reset the auto-advance timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % steps.length);
        setProgress(0);
      }, 4000);
    }
  };

  return (
    <div 
      ref={sectionRef}
      className="relative py-16 overflow-hidden"
    >
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 transition-all duration-1000"
          style={{
            background: `radial-gradient(ellipse 80% 50% at 50% 50%, ${steps[activeStep].color}15, transparent)`
          }}
        ></div>
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-4">
            How It{' '}
            <span className="bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#F97316] bg-clip-text text-transparent">
              Works
            </span>
          </h2>
          <p className="text-[var(--text-muted)] max-w-xl mx-auto">
            Our streamlined process ensures your project is delivered on time and exceeds expectations
          </p>
        </div>

        {/* Animated Timeline */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-[var(--border)] -translate-y-1/2 z-0">
            <div 
              className="h-full transition-all duration-500 ease-out"
              style={{ 
                width: `${(activeStep / (steps.length - 1)) * 100}%`,
                background: `linear-gradient(90deg, ${steps[0].color}, ${steps[activeStep].color})`
              }}
            ></div>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4 relative z-10">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === activeStep;
              const isPast = index < activeStep;

              return (
                <div
                  key={index}
                  className={`flex flex-col items-center cursor-pointer group transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                  onClick={() => handleStepClick(index)}
                >
                  {/* Step Circle */}
                  <div 
                    className={`relative w-20 h-20 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 ${
                      isActive 
                        ? 'scale-110 shadow-2xl' 
                        : isPast 
                          ? 'scale-100' 
                          : 'scale-90 opacity-60 group-hover:opacity-100 group-hover:scale-100'
                    }`}
                    style={{ 
                      background: isActive || isPast 
                        ? `linear-gradient(135deg, ${step.color}, ${step.color}CC)` 
                        : 'var(--bg-elevated)',
                      border: `2px solid ${isActive || isPast ? step.color : 'var(--border)'}`,
                      boxShadow: isActive ? `0 20px 40px ${step.color}40` : 'none'
                    }}
                  >
                    <Icon 
                      size={32} 
                      className={`transition-all duration-300 ${
                        isActive || isPast ? 'text-white' : 'text-[var(--text-muted)]'
                      }`}
                    />

                    {/* Pulse Animation for Active */}
                    {isActive && (
                      <>
                        <div 
                          className="absolute inset-0 rounded-2xl animate-ping opacity-30"
                          style={{ background: step.color }}
                        ></div>
                        <div 
                          className="absolute -inset-2 rounded-2xl opacity-20"
                          style={{ 
                            background: `radial-gradient(circle, ${step.color}, transparent 70%)`,
                            animation: 'pulse 2s infinite'
                          }}
                        ></div>
                      </>
                    )}

                    {/* Check mark for completed steps */}
                    {isPast && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-[#10B981] rounded-full flex items-center justify-center border-2 border-[var(--bg-card)]">
                        <CheckCircle size={14} className="text-white" />
                      </div>
                    )}
                  </div>

                  {/* Step Number Badge */}
                  <div 
                    className={`text-xs font-bold mb-2 px-3 py-1 rounded-full transition-all duration-300 ${
                      isActive 
                        ? 'text-white' 
                        : 'text-[var(--text-muted)] bg-[var(--bg-elevated)]'
                    }`}
                    style={isActive ? { background: step.color } : {}}
                  >
                    Step {index + 1}
                  </div>

                  {/* Step Title */}
                  <h3 
                    className={`text-lg font-bold mb-2 transition-all duration-300 ${
                      isActive ? '' : 'text-[var(--text-secondary)]'
                    }`}
                    style={isActive ? { color: step.color } : {}}
                  >
                    {step.title}
                  </h3>

                  {/* Step Description - Only show for active */}
                  <p 
                    className={`text-center text-sm transition-all duration-500 max-w-[180px] ${
                      isActive 
                        ? 'text-[var(--text-secondary)] opacity-100 max-h-20' 
                        : 'text-[var(--text-muted)] opacity-0 max-h-0 overflow-hidden md:opacity-60 md:max-h-20'
                    }`}
                  >
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Active Step Detail Card */}
        <div 
          className="mt-12 bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-8 transition-all duration-500"
          style={{ borderColor: `${steps[activeStep].color}30` }}
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Icon */}
            <div 
              className="w-24 h-24 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${steps[activeStep].color}20, ${steps[activeStep].color}10)` }}
            >
              {(() => {
                const ActiveIcon = steps[activeStep].icon;
                return <ActiveIcon size={48} style={{ color: steps[activeStep].color }} />;
              })()}
            </div>

            {/* Content */}
            <div className="flex-1 text-center md:text-left">
              <div 
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3"
                style={{ background: `${steps[activeStep].color}20`, color: steps[activeStep].color }}
              >
                Phase {activeStep + 1} of {steps.length}
              </div>
              <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
                {steps[activeStep].title}
              </h3>
              <p className="text-[var(--text-secondary)] mb-4">
                {steps[activeStep].desc}
              </p>
              
              {/* Progress Bar */}
              <div className="w-full bg-[var(--bg-elevated)] h-2 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-100"
                  style={{ 
                    width: `${progress}%`,
                    background: `linear-gradient(90deg, ${steps[activeStep].color}, ${steps[activeStep].color}CC)`
                  }}
                ></div>
              </div>
            </div>

            {/* Next Step Preview */}
            {activeStep < steps.length - 1 && (
              <div className="hidden lg:flex items-center gap-4 text-[var(--text-muted)]">
                <ArrowRight size={24} />
                <div className="text-sm">
                  <div className="opacity-60">Next:</div>
                  <div className="font-semibold text-[var(--text-secondary)]">
                    {steps[activeStep + 1].title}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Step Indicators (Mobile) */}
        <div className="flex justify-center gap-2 mt-6 md:hidden">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => handleStepClick(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === activeStep ? 'w-8' : ''
              }`}
              style={{ background: index === activeStep ? step.color : 'var(--text-muted)' }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HowItWorksAnimated;
